import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { FaEye, FaPrint, FaFileExcel, FaFilePdf, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { getCustomerStatement, clearCustomerStatement, clearError, clearSuccess, getCustomersForReport, getVendorsForReport } from '../../../../redux/Slices/AccountingReportsSlice';
import { useTitle } from '../../../../shared/hooks/TitleContext';
import { calculatePdfPagination, convertArabicNumbersToEnglish } from '../../../../shared/utils.js/utils';
import {
  generatePdfFromElementIds,
  waitForRender,
} from '../../../../shared/pdf/pdfEngine';
import * as XLSX from 'xlsx';
import i18n from '../../../../i18n/i18n';
import PdfPreviewModal from './components/PdfPreviewModal';

const CustomerStatement = () => {
  const dispatch = useDispatch();
  const { setTitle } = useTitle();
  const { t } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const textAlign = isRTL ? 'right' : 'left';
  const dir = isRTL ? 'rtl' : 'ltr';
  const publicUrl = process.env.PUBLIC_URL || '';
  const headerLogo = `${publicUrl}/ACE.png`;

  const { customerStatement, customers, isLoading, error, success } = useSelector((state) => state.accountingReports);

  const [customerId, setCustomerId] = useState('');
  const [selectedCustomerObj, setSelectedCustomerObj] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [customerPage, setCustomerPage] = useState(1);
  const customerSearchInputRef = useRef(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfType, setPdfType] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewZoom, setPreviewZoom] = useState(1.0);
  const [previewPosition, setPreviewPosition] = useState({ x: 0, y: 0 });
  const [allMovements, setAllMovements] = useState([]);
  const [localPage, setLocalPage] = useState(1);
  const [pdfPages, setPdfPages] = useState([]);
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');

  const SORT_FIELDS = { DEBIT: 'debit', CREDIT: 'credit', BALANCE: 'balance' };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <FaSort style={{ marginLeft: '4px', color: '#ccc', verticalAlign: 'middle' }} />;
    return sortOrder === 'asc'
      ? <FaSortUp style={{ marginLeft: '4px', color: '#ffd700', verticalAlign: 'middle' }} />
      : <FaSortDown style={{ marginLeft: '4px', color: '#ffd700', verticalAlign: 'middle' }} />;
  };

  const itemsPerPage = 10;

  useEffect(() => {
    setTitle('accountingReports.customerStatementTitle', { defaultValue: 'قوائم العملاء' });
    dispatch(getCustomersForReport({ page: 1 }));
    return () => {
      dispatch(clearCustomerStatement());
    };
  }, [dispatch, setTitle]);

  useEffect(() => {
    if (success) {
      toast.success(t(success) || success, { rtl: isRTL });
      dispatch(clearSuccess());
    }
  }, [success, dispatch, isRTL, t]);

  useEffect(() => {
    if (error) {
      toast.error(t(error) || error, { rtl: isRTL });
      setTimeout(() => dispatch(clearError()), 2500);
    }
  }, [error, dispatch, isRTL, t]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (customerSearchTerm) {
        dispatch(getCustomersForReport({ search: customerSearchTerm, page: 1 }));
        setCustomerPage(1);
      } else {
        dispatch(getCustomersForReport({ page: customerPage }));
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [customerSearchTerm, dispatch]);

  useEffect(() => {
    if (customerDropdownOpen && !customerSearchTerm) {
      dispatch(getCustomersForReport({ page: customerPage }));
    }
  }, [customerPage, customerDropdownOpen, customerSearchTerm, dispatch]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (customerDropdownOpen && !e.target.closest('.customer-dropdown-container')) {
        setCustomerDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [customerDropdownOpen]);

  const filteredCustomers = useMemo(() => {
    if (!Array.isArray(customers)) return [];
    if (!customerSearchTerm) return customers;
    const term = customerSearchTerm.toLowerCase();
    return customers.filter((customer) => {
      const nameAr = customer.name_ar?.toLowerCase() || '';
      const nameEn = customer.name?.toLowerCase() || '';
      const code = customer.code?.toLowerCase() || '';
      return nameAr.includes(term) || nameEn.includes(term) || code.includes(term);
    });
  }, [customers, customerSearchTerm]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!customerId) {
      toast.error(t('accountingReports.selectCustomer', { defaultValue: 'برجاء اختيار العميل أولاً' }), { rtl: isRTL });
      return;
    }
    // First fetch to get total
    const result = await dispatch(
      getCustomerStatement({
        customer_id: customerId,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        page: 1,
      })
    );
    // Then fetch all with per_page = total
    if (result.payload?.data?.movements?.total) {
      const total = result.payload.data.movements.total;
      if (total > 0) {
        await dispatch(
          getCustomerStatement({
            customer_id: customerId,
            start_date: startDate || undefined,
            end_date: endDate || undefined,
            per_page: total,
          })
        );
      }
    }
    setLocalPage(1);
  };

  const preparePdfPages = () => {
    const topHeader = document.getElementById('customer-statement-pdf-measure-top-header');
    const thead = document.getElementById('customer-statement-pdf-measure-thead');
    const footer = document.getElementById('customer-statement-pdf-measure-footer');
    const tbody = document.getElementById('customer-statement-pdf-measure-tbody');
    const chunks = calculatePdfPagination(allMovements, { topHeader, thead, footer, tbody }, { safeMargin: 25 });
    if (chunks.length === 0) {
      return [{ pageNumber: 1, movements: [], startIndex: 0, isFirstPage: true, isLastPage: true }];
    }
    return chunks.map((chunk, idx) => ({
      pageNumber: idx + 1,
      movements: chunk.items || chunk,
      startIndex: chunk.startIndex ?? idx * 10,
      isFirstPage: idx === 0,
      isLastPage: idx === chunks.length - 1,
    }));
  };

  const handlePreviewAll = () => {
    if (!customerStatement) {
      toast.error(t('accountingReports.noData', { defaultValue: 'لا توجد بيانات للمعاينة' }), { rtl: isRTL });
      return;
    }
    const pages = preparePdfPages();
    setPdfPages(pages);
    setIsPreviewOpen(true);
    setPreviewZoom(1.0);
    setPreviewPosition({ x: 0, y: 0 });
  };

  const handlePrintAll = async () => {
    if (!customerStatement) {
      toast.error(t('accountingReports.noData', { defaultValue: 'لا توجد بيانات للطباعة' }), { rtl: isRTL });
      return;
    }

    try {
      setPdfType('all');
      const pages = preparePdfPages();
      setPdfPages(pages);

      await new Promise((resolve) => requestAnimationFrame(resolve));
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }
      await new Promise((resolve) => setTimeout(resolve, 300));
      setIsGeneratingPdf(true);

      await waitForRender(300);

      // Shared PDF Engine — capture + assembly (one A4 page per chunk)
      const customerName = selectedCustomer
        ? (isRTL
            ? selectedCustomer.name_ar || selectedCustomer.name || ''
            : selectedCustomer.name || selectedCustomer.name_ar || '')
        : '';
      const pageElementIds = pages.map((_, i) =>
        document.getElementById(`customer-statement-pdf-all-${i}`)
          ? `customer-statement-pdf-all-${i}`
          : `preview-customer-statement-pdf-all-${i}`,
      );
      await generatePdfFromElementIds({
        elementIds: pageElementIds,
        fileName: `ACE_CustomerStatement_${customerName || customerId}_${new Date().toISOString().split('T')[0]}.pdf`,
        isRTL,
      });
      toast.success(t('accountingReports.printed', { defaultValue: 'تم طباعة التقرير بنجاح' }), { rtl: isRTL });
    } catch (error) {
      toast.error(t('accountingReports.printError', { defaultValue: 'حدث خطأ أثناء إنشاء PDF' }), { rtl: isRTL });
    } finally {
      setIsGeneratingPdf(false);
      setPdfType(null);
      setPdfPages([]);
    }
  };

  const handleExportAllToExcel = () => {
    if (!customerStatement) {
      toast.error(t('accountingReports.noData', { defaultValue: 'لا توجد بيانات للتصدير' }), { rtl: isRTL });
      return;
    }

    try {
      const data = customerStatement?.data || customerStatement || {};
      const totals = data.totals || {};
      const movements = data.movements?.data || [];
      const customerName = selectedCustomer ? (isRTL ? (selectedCustomer.name_ar || selectedCustomer.name || '') : (selectedCustomer.name || selectedCustomer.name_ar || '')) : customerId;

      const excelData = [
        {
          [t('accountingReports.customer', { defaultValue: 'العميل' })]: customerName,
          [t('accountingReports.period', { defaultValue: 'الفترة' })]: `${formatDate(startDate)} - ${formatDate(endDate)}`,
        },
        {},
        {
          [t('accountingReports.openingBalance', { defaultValue: 'الرصيد الافتتاحي' })]: formatNumber(totals.opening_balance || 0),
          [t('accountingReports.totalDebit', { defaultValue: 'إجمالي مدين' })]: formatNumber(totals.total_debit || 0),
          [t('accountingReports.totalCredit', { defaultValue: 'إجمالي دائن' })]: formatNumber(totals.total_credit || 0),
          [t('accountingReports.closingBalance', { defaultValue: 'الرصيد الختامي' })]: formatNumber(totals.closing_balance || 0),
        },
        {},
        {
          [t('accountingReports.debit', { defaultValue: 'مدين' })]: t('accountingReports.debit', { defaultValue: 'مدين' }),
          [t('accountingReports.credit', { defaultValue: 'دائن' })]: t('accountingReports.credit', { defaultValue: 'دائن' }),
          [t('accountingReports.balance', { defaultValue: 'الرصيد' })]: t('accountingReports.balance', { defaultValue: 'الرصيد' }),
        },
        ...movements.map((movement) => ({
          [t('accountingReports.debit', { defaultValue: 'مدين' })]: formatNumber(movement.debit || 0),
          [t('accountingReports.credit', { defaultValue: 'دائن' })]: formatNumber(movement.credit || 0),
          [t('accountingReports.balance', { defaultValue: 'الرصيد' })]: formatNumber(movement.balance || movement.running_balance || 0),
        })),
        {},
        {
          [t('accountingReports.totalDebit', { defaultValue: 'إجمالي مدين' })]: formatNumber(totals.total_debit || 0),
          [t('accountingReports.totalCredit', { defaultValue: 'إجمالي دائن' })]: formatNumber(totals.total_credit || 0),
          [t('accountingReports.closingBalance', { defaultValue: 'الرصيد الختامي' })]: formatNumber(totals.closing_balance || 0),
        },
      ];

      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Customer Statement');
      XLSX.writeFile(workbook, `CustomerStatement_${customerName || customerId}_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success(t('accountingReports.exported', { defaultValue: 'تم تصدير البيانات بنجاح' }), { rtl: isRTL });
    } catch (error) {
      toast.error(error.message || t('accountingReports.exportError', { defaultValue: 'فشل في تصدير البيانات' }), { rtl: isRTL });
    }
  };

  const formatNumber = (value) =>
    convertArabicNumbersToEnglish(
      Number(value ?? 0).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    );

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return convertArabicNumbersToEnglish(
      new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      })
    );
  };

  const reportData = customerStatement?.data || customerStatement || {};
  const selectedCustomer = selectedCustomerObj || customers.find(c => String(c.id) === String(customerId));

  // Update allMovements when customerStatement changes
  useEffect(() => {
    const movements = reportData?.movements?.data || [];
    if (movements.length > 0) {
      setAllMovements(movements);
      setLocalPage(1);
    } else {
      setAllMovements([]);
    }
  }, [customerStatement, reportData?.movements?.data]);

  // Sorting
  const sortedMovements = useMemo(() => {
    if (!sortField) return allMovements;
    return [...allMovements].sort((a, b) => {
      let aVal = a[sortField] ?? '';
      let bVal = b[sortField] ?? '';
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
      }
      return sortOrder === 'asc'
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
  }, [allMovements, sortField, sortOrder]);

  // Local pagination
  const totalPages = Math.ceil(sortedMovements.length / itemsPerPage);
  const paginatedMovements = useMemo(() => {
    const startIndex = (localPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sortedMovements.slice(startIndex, endIndex);
  }, [sortedMovements, localPage]);

  const handleLocalPageChange = (page) => {
    setLocalPage(page);
  };

  const movementsData = paginatedMovements.length > 0 ? paginatedMovements : [];

  return (
    <div style={{ padding: '20px', direction: isRTL ? 'rtl' : 'ltr' }}>
      <div style={{
        backgroundColor: '#1b3350',
        borderRadius: '12px',
        padding: '24px',
        border: '1px solid #333'
      }}>
        <h2 style={{ color: 'white', marginBottom: '20px', direction: dir, textAlign }}>
          {t('accountingReports.customerStatement', { defaultValue: 'قوائم العملاء' })}
        </h2>

        {/* Filters */}
        <form onSubmit={handleSubmit} style={{
          backgroundColor: '#1a1f2e',
          padding: '20px',
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '15px' }}>
            <div style={{ position: 'relative' }} className="customer-dropdown-container">
              <label style={{ color: '#ccc', fontSize: '14px', display: 'block', marginBottom: '8px', direction: dir, textAlign }}>
                {t('accountingReports.customer', { defaultValue: 'العميل' })} <span style={{ color: '#dc3545' }}>*</span>
              </label>
              <div
                onClick={() => {
                  setCustomerDropdownOpen(!customerDropdownOpen);
                  if (!customerDropdownOpen) {
                    setTimeout(() => {
                      if (customerSearchInputRef.current) {
                        customerSearchInputRef.current.focus();
                      }
                    }, 100);
                  }
                }}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#14263b',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  color: 'white',
                  fontSize: '16px',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <span>
                  {selectedCustomer ? (selectedCustomer.name_ar || selectedCustomer.name || `#${customerId}`) : t('accountingReports.selectCustomer', { defaultValue: 'اختر العميل' })}
                </span>
                <span>▼</span>
              </div>
              {customerDropdownOpen && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: '#14263b',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  marginTop: '4px',
                  maxHeight: '400px',
                  overflowY: 'auto',
                  zIndex: 1000,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                }}>
                  <div style={{ padding: '8px', borderBottom: '1px solid #333' }}>
                    <input
                      ref={customerSearchInputRef}
                      type="text"
                      placeholder={t('accountingReports.searchCustomer', { defaultValue: 'بحث عن عميل...' })}
                      value={customerSearchTerm}
                      onChange={(e) => setCustomerSearchTerm(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        width: '100%',
                        padding: '8px',
                        backgroundColor: '#0d1117',
                        border: 'none',
                        borderBottom: '1px solid #333',
                        color: 'white',
                        borderRadius: '6px 6px 0 0'
                      }}
                    />
                  </div>
                  {filteredCustomers.length === 0 ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#ccc' }}>
                      {t('accountingReports.noData', { defaultValue: 'لا توجد بيانات' })}
                    </div>
                  ) : (
                    <>
                      {filteredCustomers.map((customer) => (
                        <div
                          key={customer.id}
                          onClick={() => {
                            setCustomerId(String(customer.id));
                            setSelectedCustomerObj(customer);
                            setCustomerDropdownOpen(false);
                            setCustomerSearchTerm('');
                          }}
                          style={{
                            padding: '10px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #333',
                            color: String(customerId) === String(customer.id) ? '#AC2000' : 'white',
                            backgroundColor: String(customerId) === String(customer.id) ? '#2a2a2a' : 'transparent'
                          }}
                        >
                          {customer.code ? `${customer.code} - ` : ''}{isRTL ? (customer.name_ar || customer.name || `#${customer.id}`) : (customer.name || customer.name_ar || `#${customer.id}`)}
                        </div>
                      ))}
                    </>
                  )}
                  <div style={{ padding: '10px', borderTop: '1px solid #333', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setCustomerPage(prev => Math.max(1, prev - 1));
                      }}
                      disabled={customerPage === 1}
                      style={{
                        padding: '5px 10px',
                        backgroundColor: customerPage === 1 ? '#666' : '#AC2000',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: customerPage === 1 ? 'not-allowed' : 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      ‹
                    </button>
                    <span style={{ color: '#ccc', fontSize: '12px', flex: 1, textAlign: 'center' }}>
                      {t('accountingReports.page', { defaultValue: 'صفحة' })} {customerPage}
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setCustomerPage(prev => prev + 1);
                      }}
                      style={{
                        padding: '5px 10px',
                        backgroundColor: '#AC2000',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      ›
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div>
              <label style={{ color: '#ccc', fontSize: '14px', display: 'block', marginBottom: '8px', direction: dir, textAlign }}>
                {t('accountingReports.startDate', { defaultValue: 'تاريخ البداية' })}
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#14263b',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  color: 'white',
                  fontSize: '16px'
                }}
              />
            </div>
            <div>
              <label style={{ color: '#ccc', fontSize: '14px', display: 'block', marginBottom: '8px', direction: dir, textAlign }}>
                {t('accountingReports.endDate', { defaultValue: 'تاريخ النهاية' })}
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#14263b',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  color: 'white',
                  fontSize: '16px'
                }}
              />
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '15px' }}>
            <button
              type="submit"
              disabled={isLoading}
              style={{
                backgroundColor: isLoading ? '#666' : '#AC2000',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                fontSize: '16px',
                opacity: isLoading ? 0.6 : 1
              }}
            >
              {isLoading ? t('accountingReports.loading', { defaultValue: 'جاري التحميل...' }) : t('accountingReports.view', { defaultValue: 'عرض' })}
            </button>
          </div>
        </form>

        {/* Action Buttons */}
        {customerStatement && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
            <button
              onClick={handlePreviewAll}
              style={{
                backgroundColor: '#444',
                color: 'white',
                border: 'none',
                padding: '12px 20px',
                borderRadius: 8,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                justifyContent: 'center'
              }}
            >
              <FaEye /> {t('accountingReports.previewAllPDF', { defaultValue: 'عرض PDF للكل' })}
            </button>
            <button
              onClick={handlePrintAll}
              disabled={isGeneratingPdf}
              style={{
                backgroundColor: isGeneratingPdf ? '#666' : '#17a2b8',
                color: 'white',
                border: 'none',
                padding: '12px 20px',
                borderRadius: 8,
                cursor: isGeneratingPdf ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                justifyContent: 'center',
                opacity: isGeneratingPdf ? 0.6 : 1
              }}
            >
              <FaPrint /> {isGeneratingPdf ? t('accountingReports.generating', { defaultValue: 'جاري التوليد...' }) : t('accountingReports.printAllPDF', { defaultValue: 'طباعة PDF للكل' })}
            </button>
            <button
              onClick={handleExportAllToExcel}
              style={{
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                padding: '12px 20px',
                borderRadius: 8,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                justifyContent: 'center'
              }}
            >
              <FaFileExcel /> {t('accountingReports.exportExcel', { defaultValue: 'تصدير Excel للكل' })}
            </button>
          </div>
        )}

        {/* Report Data Display */}
        {customerStatement && (
          <div style={{
            backgroundColor: '#1a1f2e',
            padding: '20px',
            borderRadius: '8px',
            marginTop: '20px'
          }}>
            <div style={{ color: '#ccc', marginBottom: '20px', direction: dir, textAlign }}>
              <div style={{ marginBottom: '8px' }}>
                <strong>{t('accountingReports.customer', { defaultValue: 'العميل' })}:</strong> {selectedCustomer ? (selectedCustomer.name_ar || selectedCustomer.name) : customerId}
              </div>
              <div>
                <strong>{t('accountingReports.period', { defaultValue: 'الفترة' })}:</strong> {formatDate(startDate)} - {formatDate(endDate)}
              </div>
            </div>

            {/* Summary Cards */}
            {reportData.totals && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '30px' }}>
                <div style={{ padding: '15px', backgroundColor: '#1b3350', borderRadius: '6px', border: '1px solid #333' }}>
                  <div style={{ color: '#9ca3af', fontSize: '14px', marginBottom: '5px', direction: dir, textAlign }}>
                    {t('accountingReports.openingBalance', { defaultValue: 'الرصيد الافتتاحي' })}
                  </div>
                  <div style={{ color: 'white', fontSize: '18px', fontWeight: 'bold', direction: dir, textAlign }}>
                    {formatNumber(reportData.totals.opening_balance || 0)}
                  </div>
                </div>
                <div style={{ padding: '15px', backgroundColor: '#1b3350', borderRadius: '6px', border: '1px solid #333' }}>
                  <div style={{ color: '#9ca3af', fontSize: '14px', marginBottom: '5px', direction: dir, textAlign }}>
                    {t('accountingReports.totalDebit', { defaultValue: 'إجمالي مدين' })}
                  </div>
                  <div style={{ color: '#dc3545', fontSize: '18px', fontWeight: 'bold', direction: dir, textAlign }}>
                    {formatNumber(reportData.totals.total_debit || 0)}
                  </div>
                </div>
                <div style={{ padding: '15px', backgroundColor: '#1b3350', borderRadius: '6px', border: '1px solid #333' }}>
                  <div style={{ color: '#9ca3af', fontSize: '14px', marginBottom: '5px', direction: dir, textAlign }}>
                    {t('accountingReports.totalCredit', { defaultValue: 'إجمالي دائن' })}
                  </div>
                  <div style={{ color: '#28a745', fontSize: '18px', fontWeight: 'bold', direction: dir, textAlign }}>
                    {formatNumber(reportData.totals.total_credit || 0)}
                  </div>
                </div>
                <div style={{ padding: '15px', backgroundColor: '#1b3350', borderRadius: '6px', border: '1px solid #333' }}>
                  <div style={{ color: '#9ca3af', fontSize: '14px', marginBottom: '5px', direction: dir, textAlign }}>
                    {t('accountingReports.closingBalance', { defaultValue: 'الرصيد الختامي' })}
                  </div>
                  <div style={{ color: 'white', fontSize: '18px', fontWeight: 'bold', direction: dir, textAlign }}>
                    {formatNumber(reportData.totals.closing_balance || 0)}
                  </div>
                </div>
              </div>
            )}

            {/* Movements Table */}
            {reportData.movements && allMovements.length > 0 && (
              <div>
                <h3 style={{ color: 'white', marginBottom: '15px', fontSize: '18px', direction: dir, textAlign }}>
                  {t('accountingReports.movements', { defaultValue: 'الحركات' })}
                </h3>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: '#1b3350' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#AC2000' }}>
                        <th style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333', width: '36px' }}>#</th>
                        <th style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333', cursor: 'pointer' }} onClick={() => handleSort(SORT_FIELDS.DEBIT)}>
                          {t('accountingReports.debit', { defaultValue: 'مدين' })}{getSortIcon(SORT_FIELDS.DEBIT)}
                        </th>
                        <th style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333', cursor: 'pointer' }} onClick={() => handleSort(SORT_FIELDS.CREDIT)}>
                          {t('accountingReports.credit', { defaultValue: 'دائن' })}{getSortIcon(SORT_FIELDS.CREDIT)}
                        </th>
                        <th style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333', cursor: 'pointer' }} onClick={() => handleSort(SORT_FIELDS.BALANCE)}>
                          {t('accountingReports.balance', { defaultValue: 'الرصيد' })}{getSortIcon(SORT_FIELDS.BALANCE)}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {allMovements.length > 0 ? (
                        movementsData.map((movement, idx) => (
                          <tr key={movement.id || idx} style={{ backgroundColor: idx % 2 === 0 ? '#1b3350' : '#1a1f2e' }}>
                            <td style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333' }}>
                              {(localPage - 1) * itemsPerPage + idx + 1}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333' }}>
                              {formatNumber(movement.debit || 0)}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333' }}>
                              {formatNumber(movement.credit || 0)}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333' }}>
                              {formatNumber(movement.balance || movement.running_balance || 0)}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={4} style={{ padding: '30px', textAlign: 'center', color: '#9ca3af' }}>
                            {t('accountingReports.noMovements', { defaultValue: 'لا توجد حركات في الفترة المحددة' })}
                          </td>
                        </tr>
                      )}
                      {reportData.totals && allMovements.length > 0 && (
                        <tr style={{ backgroundColor: '#1f2937', fontWeight: 'bold' }}>
                          <td style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333' }}>
                            {t('accountingReports.total', { defaultValue: 'الإجمالي' })}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333' }}>
                            {formatNumber(reportData.totals.total_debit || 0)}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333' }}>
                            {formatNumber(reportData.totals.total_credit || 0)}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333' }}>
                            {formatNumber(reportData.totals.closing_balance || 0)}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {customerId && allMovements.length > 0 && totalPages > 1 && (
                  <div
                    style={{
                      padding: '18px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '8px',
                      borderTop: '1px solid #333',
                    }}
                  >
                    <button
                      onClick={() => handleLocalPageChange(Math.max(1, localPage - 1))}
                      disabled={localPage === 1}
                      style={{
                        backgroundColor: '#1b3350',
                        color: 'white',
                        border: '1px solid #333',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        cursor: localPage > 1 ? 'pointer' : 'not-allowed',
                        opacity: localPage > 1 ? 1 : 0.5,
                        fontSize: '14px',
                      }}
                    >
                      &lt;
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={`page-${page}`}
                        onClick={() => handleLocalPageChange(page)}
                        style={{
                          backgroundColor:
                            page === localPage ? '#AC2000' : '#1b3350',
                          color: 'white',
                          border:
                            page === localPage ? 'none' : '1px solid #333',
                          padding: '8px 12px',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: page === localPage ? 'bold' : 'normal',
                        }}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={() => handleLocalPageChange(Math.min(totalPages, localPage + 1))}
                      disabled={localPage === totalPages}
                      style={{
                        backgroundColor: '#1b3350',
                        color: 'white',
                        border: '1px solid #333',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        cursor: localPage < totalPages ? 'pointer' : 'not-allowed',
                        opacity: localPage < totalPages ? 1 : 0.5,
                        fontSize: '14px',
                      }}
                    >
                      &gt;
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* PDF Print Templates (one full page per chunk) */}
      {pdfType === 'all' && customerStatement && pdfPages && pdfPages.length > 0 && (
        pdfPages.map((page, pageIndex) => (
          <CustomerStatementPdfPage
            key={pageIndex}
            idPrefix="customer-statement-pdf-all"
            page={page}
            pageIndex={pageIndex}
            isRTL={isRTL}
            dir={dir}
            t={t}
            publicUrl={publicUrl}
            headerLogo={headerLogo}
            selectedCustomer={selectedCustomer}
            customerId={customerId}
            startDate={startDate}
            endDate={endDate}
            totals={reportData.totals}
            formatNumber={formatNumber}
            formatDate={formatDate}
            hidden
          />
        ))
      )}

      <PdfPreviewModal
        isOpen={isPreviewOpen && !!customerStatement}
        title={t('accountingReports.customerStatementTitle', { defaultValue: 'قوائم العملاء' })}
        onClose={() => setIsPreviewOpen(false)}
        onPrint={handlePrintAll}
        isGeneratingPdf={isGeneratingPdf}
        contentWidth="794px"
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
          {pdfPages && pdfPages.length > 0 && pdfPages.map((page, pageIndex) => (
            <CustomerStatementPdfPage
              key={pageIndex}
              idPrefix="preview-customer-statement-pdf-all"
              page={page}
              pageIndex={pageIndex}
              isRTL={isRTL}
              dir={dir}
              t={t}
              publicUrl={publicUrl}
              headerLogo={headerLogo}
              selectedCustomer={selectedCustomer}
              customerId={customerId}
              startDate={startDate}
              endDate={endDate}
              totals={reportData.totals}
              formatNumber={formatNumber}
              formatDate={formatDate}
            />
          ))}
        </div>
      </PdfPreviewModal>

      {/* Hidden measurement template used to calculate dynamic PDF pagination */}
      <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', top: 0, width: '794px', pointerEvents: 'none', visibility: 'hidden' }}>
        <div id="customer-statement-pdf-measure-top-header" style={{ width: '100%' }}>
          <img src={`${publicUrl}/HeaderAce.png`} alt="" style={{ width: '100%', height: 'auto', objectFit: 'contain', marginBottom: '8px', borderBottom: '4px double #000', paddingBottom: '8px' }} />
          <h2 style={{ textAlign: 'center', fontSize: '20px', margin: '12px 0' }}>{t('accountingReports.customerStatementTitle', { defaultValue: 'قوائم العملاء' })}</h2>
          <div style={{ textAlign: 'center', fontSize: '14px', marginBottom: '20px' }}>{formatDate(startDate)} - {formatDate(endDate)}</div>
          {/* Reserve space for the first-page opening/closing balance summary table so pagination never overflows into the footer on page 1 */}
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '15px' }}>
            <tbody>
              <tr><td style={{ border: '1px solid #000', padding: '6px', width: '50%' }}>x</td><td style={{ border: '1px solid #000', padding: '6px' }}>0.00</td></tr>
              <tr><td style={{ border: '1px solid #000', padding: '6px' }}>x</td><td style={{ border: '1px solid #000', padding: '6px' }}>0.00</td></tr>
              <tr><td style={{ border: '1px solid #000', padding: '6px' }}>x</td><td style={{ border: '1px solid #000', padding: '6px' }}>0.00</td></tr>
              <tr><td style={{ border: '1px solid #000', padding: '6px' }}>x</td><td style={{ border: '1px solid #000', padding: '6px' }}>0.00</td></tr>
            </tbody>
          </table>
          <div style={{ marginBottom: '10px', fontWeight: 'bold', textAlign: 'center' }}>{t('accountingReports.movements', { defaultValue: 'الحركات' })}</div>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #000' }}>
          <thead id="customer-statement-pdf-measure-thead">
            <tr>
              <th style={{ border: '1px solid #000', padding: '6px' }}>#</th>
              <th style={{ border: '1px solid #000', padding: '6px' }}>{t('accountingReports.debit', { defaultValue: 'مدين' })}</th>
              <th style={{ border: '1px solid #000', padding: '6px' }}>{t('accountingReports.credit', { defaultValue: 'دائن' })}</th>
              <th style={{ border: '1px solid #000', padding: '6px' }}>{t('accountingReports.balance', { defaultValue: 'الرصيد' })}</th>
            </tr>
          </thead>
          <tbody id="customer-statement-pdf-measure-tbody">
            {allMovements.slice(0, 5).map((movement, idx) => (
              <tr key={idx}>
                <td style={{ border: '1px solid #000', padding: '6px' }}>{idx + 1}</td>
                <td style={{ border: '1px solid #000', padding: '6px' }}>{movement.debit || '—'}</td>
                <td style={{ border: '1px solid #000', padding: '6px' }}>{movement.credit || '—'}</td>
                <td style={{ border: '1px solid #000', padding: '6px' }}>{movement.balance || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div id="customer-statement-pdf-measure-footer" style={{ height: '120px' }} />
      </div>
    </div>
  );
};

const CustomerStatementPdfPage = ({
  idPrefix,
  page,
  pageIndex,
  isRTL,
  dir,
  t,
  publicUrl,
  headerLogo,
  selectedCustomer,
  customerId,
  startDate,
  endDate,
  totals,
  formatNumber,
  formatDate,
  hidden = false,
}) => {
  const movements = page.movements || [];
  return (
    <div
      id={`${idPrefix}-${pageIndex}`}
      aria-hidden={hidden || undefined}
      style={{
        position: hidden ? 'absolute' : 'static',
        left: hidden ? '-9999px' : undefined,
        top: hidden ? 0 : undefined,
        width: '794px',
        minHeight: '1123px',
        height: 'auto',
        backgroundColor: '#ffffff',
        color: '#000000',
        fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
        fontWeight: 'bold',
        padding: '40px',
        boxSizing: 'border-box',
        overflow: 'visible',
        boxShadow: hidden ? undefined : '0 4px 20px rgba(0, 0, 0, 0.5)',
      }}
    >
      <div style={{
        border: '5px solid #000',
        padding: '3px',
        backgroundColor: '#ffffff',
        boxSizing: 'border-box',
        width: '100%',
        minHeight: '1043px',
        height: 'auto',
        overflow: 'visible'
      }}>
        <div style={{
          border: '2px solid #000',
          minHeight: '1033px',
          height: '1033px',
          padding: '18px 18px 10px',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
          fontWeight: 'bold',
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          textRendering: 'optimizeLegibility',
          direction: isRTL ? 'rtl' : 'ltr',
          backgroundColor: '#ffffff',
          boxSizing: 'border-box',
          width: '100%',
          overflow: 'visible'
        }}>
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '100%',
            height: '600px',
            backgroundImage: `url(${headerLogo})`,
            backgroundSize: '95%',
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat',
            opacity: 0.1,
            zIndex: 0,
            pointerEvents: 'none'
          }} />
          <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%', minHeight: '100%' }}>
            <img
              src={`${publicUrl}/HeaderAce.png`}
              alt="Header"
              style={{
                width: '100%',
                height: 'auto',
                objectFit: 'contain',
                marginBottom: '8px',
                borderBottom: '4px double #000',
                paddingBottom: '8px',
                flexShrink: 0
              }}
              crossOrigin="anonymous"
            />

            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'visible', minHeight: 0, paddingBottom: '120px' }}>
              <div style={{ marginTop: '12px', textAlign: 'center', marginBottom: '20px', direction: dir }}>
                <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#000080', margin: 0, direction: dir }}>
                  {t('accountingReports.customerStatementTitle', { defaultValue: 'قوائم العملاء' })}
                </h2>
                <div style={{ fontSize: '14px', marginTop: '8px', direction: dir }}>
                  {selectedCustomer
                    ? (isRTL ? (selectedCustomer.name_ar || selectedCustomer.name || '') : (selectedCustomer.name || selectedCustomer.name_ar || ''))
                    : customerId}
                </div>
                <div style={{ fontSize: '14px', marginTop: '4px', direction: dir }}>
                  {formatDate(startDate)} - {formatDate(endDate)}
                </div>
              </div>
              <div style={{ color: '#000', fontSize: '12px' }}>
                {page.isFirstPage && totals && (
                  <div style={{ marginBottom: '20px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '15px', direction: isRTL ? 'rtl' : 'ltr' }}>
                      <tbody>
                        <tr>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: isRTL ? 'right' : 'left', width: '50%', fontWeight: 'bold', direction: dir }}>
                            {t('accountingReports.openingBalance', { defaultValue: 'الرصيد الافتتاحي' })}
                          </td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', width: '50%' }}>
                            {formatNumber(totals.opening_balance || 0)}
                          </td>
                        </tr>
                        <tr>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: isRTL ? 'right' : 'left', fontWeight: 'bold', direction: dir }}>
                            {t('accountingReports.totalDebit', { defaultValue: 'إجمالي مدين' })}
                          </td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                            {formatNumber(totals.total_debit || 0)}
                          </td>
                        </tr>
                        <tr>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: isRTL ? 'right' : 'left', fontWeight: 'bold', direction: dir }}>
                            {t('accountingReports.totalCredit', { defaultValue: 'إجمالي دائن' })}
                          </td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                            {formatNumber(totals.total_credit || 0)}
                          </td>
                        </tr>
                        <tr>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: isRTL ? 'right' : 'left', fontWeight: 'bold', direction: dir }}>
                            {t('accountingReports.closingBalance', { defaultValue: 'الرصيد الختامي' })}
                          </td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                            {formatNumber(totals.closing_balance || 0)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
                <div>
                  <div style={{ marginBottom: '10px', fontWeight: 'bold', textAlign: 'center', direction: dir }}>
                    {t('accountingReports.movements', { defaultValue: 'الحركات' })}
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #000' }}>
                    <thead>
                      <tr style={{ backgroundColor: 'rgba(238, 242, 247, 0.7)', fontWeight: 'bold' }}>
                        <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', width: '30px' }}>#</th>
                        <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                          {t('accountingReports.debit', { defaultValue: 'مدين' })}
                        </th>
                        <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                          {t('accountingReports.credit', { defaultValue: 'دائن' })}
                        </th>
                        <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                          {t('accountingReports.balance', { defaultValue: 'الرصيد' })}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {movements.length === 0 && (
                        <tr>
                          <td colSpan={4} style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>
                            {t('accountingReports.noData', { defaultValue: 'لا توجد بيانات' })}
                          </td>
                        </tr>
                      )}
                      {movements.map((movement, idx) => (
                        <tr key={movement.id || idx} style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                            {page.startIndex + idx + 1}
                          </td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                            {formatNumber(movement.debit || 0)}
                          </td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                            {formatNumber(movement.credit || 0)}
                          </td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                            {formatNumber(movement.balance || movement.running_balance || 0)}
                          </td>
                        </tr>
                      ))}
                      {page.isLastPage && totals && (
                        <tr style={{ backgroundColor: 'rgba(238, 242, 247, 0.7)', fontWeight: 'bold' }}>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                            {t('accountingReports.total', { defaultValue: 'الإجمالي' })}
                          </td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                            {formatNumber(totals.total_debit || 0)}
                          </td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                            {formatNumber(totals.total_credit || 0)}
                          </td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                            {formatNumber(totals.closing_balance || 0)}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <div style={{
            position: 'absolute',
            bottom: '10px',
            left: '18px',
            right: '18px',
            flexShrink: 0
          }}>
            <img
              src={`${publicUrl}/FooterAce.png`}
              alt="Footer"
              style={{
                width: '100%',
                height: 'auto',
                objectFit: 'contain',
                borderTop: '4px double #000',
                paddingTop: '8px',
              }}
              crossOrigin="anonymous"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerStatement;
